"use client";

import { useEffect, useRef } from "react";

type AnimationVariant =
  | "fade-up"
  | "fade-down"
  | "fade-in-left"
  | "fade-in-right"
  | "scale-in"
  | "fade-in";

interface ScrollRevealOptions {
  animation?: AnimationVariant;
  threshold?: number;
  stagger?: boolean;
  staggerInterval?: number;
}

export function useScrollReveal<T extends HTMLElement = HTMLElement>(
  options: ScrollRevealOptions = {}
) {
  const {
    animation = "fade-up",
    threshold = 0.1,
    stagger = false,
    staggerInterval = 100,
  } = options;

  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const animClass = `animate-${animation}`;

    if (stagger) {
      const children = Array.from(el.children) as HTMLElement[];
      children.forEach((child) => child.classList.add("reveal-hidden"));

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            children.forEach((child, i) => {
              child.style.animationDelay = `${i * staggerInterval}ms`;
              child.classList.remove("reveal-hidden");
              child.classList.add(animClass);
            });
            observer.unobserve(el);
          }
        },
        { threshold }
      );

      observer.observe(el);
      return () => observer.disconnect();
    }

    el.classList.add("reveal-hidden");

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.remove("reveal-hidden");
          el.classList.add(animClass);
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [animation, threshold, stagger, staggerInterval]);

  return ref;
}
